import { memo, type PointerEventHandler, useCallback, useEffect, useState } from 'react';

import { GripVerticalIcon, Minimize2Icon, PanelRightIcon, PinIcon, PlusIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn, tailwindVariants } from '@air/utils-tailwind';

import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { PrivateWorkspaceAgentPanelTaskHeaderOverflowMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelTaskHeaderOverflowMenu';
import { PrivateWorkspaceAgentPanelTaskHeaderTitle } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelTaskHeaderTitle';
import { PrivateWorkspaceAgentPanelTasksToggleButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelTasksToggleButton';
import { useIsWorkspaceAgentPanelOverAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelOverAssetView';
import { useOpenWorkspaceAgentAssetList } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetList';
import { usePrivateWorkspaceAgentPanelSessionStatus } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPanelSessionStatus';
import { usePrivateWorkspaceAgentPinnedArtifacts } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPinnedArtifacts';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentPanelView } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelView';
import { getWorkspaceAgentMinimizeFocusRescue } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentMinimizeFocusRescue';
import { type WorkspaceAgentPanelHeaderStatus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelHeaderStatus';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import {
  isWorkspaceAgentPanelExpanded,
  useWorkspaceAgentPanelStore,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

const queryWorkspaceAgentMinimizeFocusRescueByTestId = (testId: string) =>
  // biome-ignore lint/plugin: Minimize unmounts the focused control; rescue is a mounted sibling by test id.
  document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);

const workspaceAgentStatusDot = tailwindVariants({
  base: 'size-1.5 shrink-0 rounded-full',
  variants: {
    status: {
      waiting: 'ads:bg-ads-amber-9 bg-amber-9',
      working: 'ads:bg-ads-gray-9 bg-gray-9',
      idle: 'ads:bg-ads-grass-9 bg-grass-9',
    },
  },
});

type WorkspaceAgentStatus = WorkspaceAgentPanelHeaderStatus;

const STATUS_LABELS: Record<WorkspaceAgentStatus, string> = {
  waiting: 'Waiting for you',
  working: 'Working',
  idle: 'Idle',
};

const handleNewSession = (
  resetSession: () => void,
  focusComposer: () => void,
  onPanelViewChange: (nextView: WorkspaceAgentPanelView) => void,
) => {
  resetSession();
  onPanelViewChange('chat');
  focusComposer();
};

export type PrivateWorkspaceAgentPanelHeaderProps = {
  isDragging: boolean;
  isFloating: boolean;
  onHeaderPointerDown: PointerEventHandler<HTMLElement>;
  panelView: WorkspaceAgentPanelView;
  onPanelViewChange: (nextView: WorkspaceAgentPanelView) => void;
};

export const PrivateWorkspaceAgentPanelHeader = memo(
  ({
    isDragging,
    isFloating,
    onHeaderPointerDown,
    panelView,
    onPanelViewChange,
  }: PrivateWorkspaceAgentPanelHeaderProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const { status } = usePrivateWorkspaceAgentPanelSessionStatus();
    const { assetViewOpen } = useIsWorkspaceAgentPanelOverAssetView();
    const { ui2026 } = useTemporaryFeatureFlags();
    const minimize = useWorkspaceAgentPanelStore((state) => state.minimize);
    const dockToRight = useWorkspaceAgentPanelStore((state) => state.dockToRight);
    const focusComposer = useWorkspaceAgentPanelStore((state) => state.focusComposer);
    const setOnboardingHandoff = useWorkspaceAgentPanelStore((state) => state.setOnboardingHandoff);
    const clearBoardTemplatesFlowBoard = useWorkspaceAgentPanelStore((state) => state.clearBoardTemplatesFlowBoard);
    const closeGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.closeGuidedVariantFlow);
    const setPendingThreadCreatedLocation = useWorkspaceAgentPanelStore(
      (state) => state.setPendingThreadCreatedLocation,
    );
    const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
    const isElevatedAboveOnboarding = useWorkspaceAgentPanelStore((state) => state.isElevatedAboveOnboarding);
    const isOnboardingRunInProgress = isElevatedAboveOnboarding;
    const [isRenaming, setIsRenaming] = useState(false);
    const { closeAssetListView } = useOpenWorkspaceAgentAssetList();
    const { pinnedArtifacts } = usePrivateWorkspaceAgentPinnedArtifacts();
    const pinnedItemsOpen = useWorkspaceAgentAssetListViewStore((state) => state.pinnedItemsOpen);
    const setPinnedItemsOpen = useWorkspaceAgentAssetListViewStore((state) => state.setPinnedItemsOpen);
    const hasActiveArtifactView = useWorkspaceAgentAssetListViewStore((state) => Boolean(state.activeArtifactId));

    // biome-ignore lint/correctness/useExhaustiveDependencies: reset rename when the active session changes
    useEffect(() => {
      setIsRenaming(false);
    }, [session.sessionId]);

    const handleNewSessionClick = useCallback(() => {
      if (blockIfCreditsDepleted()) {
        return;
      }

      setPendingThreadCreatedLocation('header');
      // The intro's simulated thread lives in memory, not in the session. A new thread drops it.
      setOnboardingHandoff(null);
      clearBoardTemplatesFlowBoard();
      closeGuidedVariantFlow();
      handleNewSession(session.resetSession, focusComposer, onPanelViewChange);
    }, [
      blockIfCreditsDepleted,
      clearBoardTemplatesFlowBoard,
      closeGuidedVariantFlow,
      focusComposer,
      onPanelViewChange,
      session.resetSession,
      setOnboardingHandoff,
      setPendingThreadCreatedLocation,
    ]);

    const handleMinimizeClick = useCallback(() => {
      const resolveRescue = () =>
        getWorkspaceAgentMinimizeFocusRescue({
          assetViewOpen,
          queryByTestId: queryWorkspaceAgentMinimizeFocusRescueByTestId,
          ui2026,
        });

      const { pendingFocus } = resolveRescue();

      if (hasActiveArtifactView) {
        closeAssetListView();
      }
      minimize(pendingFocus);

      requestAnimationFrame(() => {
        // A quick reopen owns focus in the panel before this deferred minimize callback runs.
        const isPanelReopened = isWorkspaceAgentPanelExpanded(useWorkspaceAgentPanelStore.getState());

        if (isPanelReopened) {
          return;
        }

        resolveRescue().rescue?.focus();
      });
    }, [assetViewOpen, closeAssetListView, hasActiveArtifactView, minimize, ui2026]);

    const handleDockToRightClick = useCallback(() => {
      dockToRight();
      requestAnimationFrame(() => {
        // biome-ignore lint/plugin: focus rescue after the dock-to-right control unmounts
        document.querySelector<HTMLElement>('[data-testid="WORKSPACE_AGENT_PANEL_MINIMIZE"]')?.focus();
      });
    }, [dockToRight]);

    const handleTogglePinnedItems = useCallback(() => {
      setPinnedItemsOpen((value) => !value);
    }, [setPinnedItemsOpen]);

    const handleRenameStart = useCallback(() => {
      setIsRenaming(true);
    }, []);

    const isChatView = panelView === 'chat';

    return (
      <header
        className={cn(
          'flex ui2026:h-14 min-w-0 shrink-0 cursor-grab touch-none items-center gap-2 ads:border-default border-gray-5 ui2026:border-gray-4 border-b py-3.5 ui2026:py-0 pr-3 ui2026:pr-4 pl-1 active:cursor-grabbing',
          isDragging && 'cursor-grabbing',
        )}
        data-testid="WORKSPACE_AGENT_PANEL_HEADER"
        onPointerDown={isRenaming ? undefined : onHeaderPointerDown}
      >
        <div
          aria-hidden
          className="flex h-full w-6 shrink-0 cursor-grab touch-none items-center justify-center active:cursor-grabbing"
          data-testid="WORKSPACE_AGENT_PANEL_FLOAT_GRIP"
        >
          <GripVerticalIcon aria-hidden className="ads:text-icon-subtle text-gray-7" size="sm" />
        </div>
        {isChatView ? (
          <>
            <span
              aria-hidden
              className={workspaceAgentStatusDot({ status })}
              data-testid="WORKSPACE_AGENT_PANEL_HEADER_STATUS_DOT"
            />
            <output className="sr-only">{STATUS_LABELS[status]}</output>
          </>
        ) : null}
        <PrivateWorkspaceAgentPanelTaskHeaderTitle
          isRenaming={isRenaming}
          onRenamingChange={setIsRenaming}
          panelView={panelView}
        />

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {isChatView && pinnedArtifacts.length > 0 ? (
            <Tooltip label="Pinned items" side="bottom" sideOffset={4}>
              <IconButton
                appearance={pinnedItemsOpen ? 'filled' : 'ghost'}
                aria-expanded={pinnedItemsOpen}
                aria-pressed={pinnedItemsOpen}
                color="gray"
                data-testid="WORKSPACE_AGENT_PINNED_ITEMS_BUTTON"
                disabled={isRenaming || isOnboardingRunInProgress}
                label="Pinned items"
                onClick={handleTogglePinnedItems}
                size="small"
              >
                <PinIcon aria-hidden className={cn(pinnedItemsOpen && 'fill-current')} size="sm" />
              </IconButton>
            </Tooltip>
          ) : null}

          <PrivateWorkspaceAgentPanelTaskHeaderOverflowMenu
            disabled={isRenaming || isOnboardingRunInProgress}
            onRename={handleRenameStart}
            panelView={panelView}
          />

          <Tooltip label="New thread" side="bottom" sideOffset={4}>
            <IconButton
              appearance="ghost"
              color="gray"
              data-testid="WORKSPACE_AGENT_NEW_SESSION_BUTTON"
              disabled={isRenaming || isCreditsDepleted || isOnboardingRunInProgress}
              label="New thread"
              onClick={handleNewSessionClick}
              size="small"
            >
              <PlusIcon aria-hidden size="sm" />
            </IconButton>
          </Tooltip>

          <PrivateWorkspaceAgentPanelTasksToggleButton
            disabled={isRenaming || isOnboardingRunInProgress}
            onPanelViewChange={onPanelViewChange}
            panelView={panelView}
          />

          {isFloating ? (
            <Tooltip label="Dock to right" side="bottom" sideOffset={4}>
              <IconButton
                appearance="ghost"
                color="gray"
                data-testid="WORKSPACE_AGENT_PANEL_DOCK_TO_RIGHT"
                disabled={isRenaming || isOnboardingRunInProgress}
                label="Dock to right"
                onClick={handleDockToRightClick}
                size="small"
              >
                <PanelRightIcon aria-hidden size="sm" />
              </IconButton>
            </Tooltip>
          ) : null}

          <Tooltip label="Minimize" side="bottom" sideOffset={4}>
            <IconButton
              appearance="ghost"
              color="gray"
              data-testid="WORKSPACE_AGENT_PANEL_MINIMIZE"
              disabled={isRenaming || isOnboardingRunInProgress}
              label="Minimize"
              onClick={handleMinimizeClick}
              size="small"
            >
              <Minimize2Icon aria-hidden size="sm" />
            </IconButton>
          </Tooltip>
        </div>
      </header>
    );
  },
);

PrivateWorkspaceAgentPanelHeader.displayName = 'PrivateWorkspaceAgentPanelHeader';
